import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import {
  addDropComment,
  listenToDropComments,
  reportDropComment,
  setCreatorCommentReply,
  softDeleteDropComment,
  toggleDropCommentLike,
} from "../services/socialService";
import ProfileAvatarLink from "./profile/ProfileAvatarLink";
import ProfileNameLink from "./profile/ProfileNameLink";
import EmptyCommentLine from "./EmptyCommentLine";

const MAX_COMMENT_LEN = 280;
const REPORT_REASONS = [
  "Spam",
  "Harassment or abuse",
  "Inappropriate content",
  "Other",
];

async function fetchCommentRank(dropId, userId) {
  if (!dropId || !userId) return null;
  try {
    const snap = await getDoc(doc(db, "first_views", `${dropId}_${userId}`));
    if (!snap.exists()) return null;
    const data = snap.data();
    return data.rank || data.viewNumber || null;
  } catch {
    return null;
  }
}

function formatCommentTime(createdAt) {
  if (!createdAt) return "";
  const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  );
}

function FlagIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  );
}

/**
 * Drop comments — middle-ground list + creator-only nested reply.
 * Flat fan comments; optional `creatorReply` subfield (never a second top-level comment).
 */
export default function DropCommentsSection({
  dropId,
  creatorId,
  creatorName = "the creator",
  creatorAvatar = "",
  hasAccess = false,
  title = null,
  limit = null,
  readOnly = false,
  hideSub = false,
  modNote = null,
}) {
  const { currentUser, userProfile } = useAuth();
  const [comments, setComments] = useState([]);
  const [ranks, setRanks] = useState({});
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [replyOpenId, setReplyOpenId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [leavingIds, setLeavingIds] = useState({});
  const [toast, setToast] = useState(null);

  const isCreatorViewer = Boolean(currentUser && creatorId && currentUser.uid === creatorId);
  const canPost = Boolean(currentUser) && hasAccess && !readOnly;
  const visibleComments = typeof limit === "number" ? comments.slice(0, limit) : comments;

  useEffect(() => {
    if (!dropId) return undefined;
    return listenToDropComments(dropId, setComments);
  }, [dropId]);

  useEffect(() => {
    let cancelled = false;
    const ids = [...new Set(comments.map((c) => c.userId).filter(Boolean))];
    if (!ids.length) return undefined;
    (async () => {
      const pairs = await Promise.all(
        ids.map(async (uid) => [uid, await fetchCommentRank(dropId, uid)])
      );
      if (!cancelled) {
        setRanks(Object.fromEntries(pairs.filter(([, rank]) => rank)));
      }
    })();
    return () => { cancelled = true; };
  }, [comments, dropId]);

  const showToast = (message, tone = "success") => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 2800);
  };

  const handlePost = async () => {
    if (!currentUser || !text.trim() || posting || !canPost) return;
    setPosting(true);
    try {
      await addDropComment({
        dropId,
        userId: currentUser.uid,
        displayName: userProfile?.displayName || currentUser.displayName || "Member",
        avatar: userProfile?.avatarUrl || userProfile?.avatar || "",
        handle: userProfile?.handle || userProfile?.username || "",
        text: text.trim().slice(0, MAX_COMMENT_LEN),
        creatorId,
      });
      setText("");
    } catch (err) {
      console.error("Drop comment error:", err);
      showToast(err.message || "Could not post comment.", "error");
    } finally {
      setPosting(false);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!currentUser) return;
    try {
      await toggleDropCommentLike({ dropId, commentId, userId: currentUser.uid });
    } catch (err) {
      console.error("Comment like error:", err);
    }
  };

  const handleConfirmDelete = async (commentId) => {
    setLeavingIds((prev) => ({ ...prev, [commentId]: true }));
    setConfirmDeleteId(null);
    try {
      await softDeleteDropComment({ dropId, commentId });
    } catch (err) {
      console.error("Delete comment error:", err);
      setLeavingIds((prev) => {
        const next = { ...prev };
        delete next[commentId];
        return next;
      });
      showToast(err.message || "Could not delete comment.", "error");
    }
  };

  const handleReport = async (commentId, reason) => {
    if (reportBusy) return;
    setReportBusy(true);
    try {
      await reportDropComment({ dropId, commentId, reason });
      setReportId(null);
      showToast("Reported — thanks for flagging this");
    } catch (err) {
      console.error("Report comment error:", err);
      showToast(err.message || "Could not submit report.", "error");
    } finally {
      setReportBusy(false);
    }
  };

  const openReplyComposer = (comment) => {
    const existing = comment.creatorReply?.text || "";
    setReplyOpenId(comment.id);
    setReplyText(existing);
    setConfirmDeleteId(null);
    setReportId(null);
  };

  const handleSendReply = async (commentId) => {
    if (!replyText.trim() || replyBusy) return;
    setReplyBusy(true);
    try {
      await setCreatorCommentReply({
        dropId,
        commentId,
        text: replyText.trim().slice(0, MAX_COMMENT_LEN),
      });
      setReplyOpenId(null);
      setReplyText("");
    } catch (err) {
      console.error("Creator reply error:", err);
      showToast(err.message || "Could not save reply.", "error");
    } finally {
      setReplyBusy(false);
    }
  };

  const firstName = (creatorName || "Creator").split(" ")[0];

  const renderComment = (comment) => {
    const isMine = Boolean(currentUser && comment.userId === currentUser.uid);
    const isCreatorComment = Boolean(
      comment.isCreator || (creatorId && comment.userId === creatorId)
    );
    const rank = comment.rank || ranks[comment.userId];
    const likedBy = Array.isArray(comment.likedBy) ? comment.likedBy : [];
    const likedByCreator = Boolean(
      comment.likedByCreator || (creatorId && likedBy.includes(creatorId))
    );
    const likeCount = typeof comment.likeCount === "number" ? comment.likeCount : likedBy.length;
    const iLiked = Boolean(currentUser && likedBy.includes(currentUser.uid));
    const commentUser = {
      uid: comment.userId,
      isCreator: isCreatorComment,
      handle: comment.handle,
    };
    const hasReply = Boolean(comment.creatorReply?.text);
    const footConfirming = confirmDeleteId === comment.id;
    const footReporting = reportId === comment.id;
    const showReplyLink = isCreatorViewer && !isCreatorComment;
    const leaving = Boolean(leavingIds[comment.id]);

    return (
      <div
        key={comment.id}
        className={[
          "dv-comment-item",
          isMine ? "mine" : "",
          isCreatorComment ? "creator" : "",
          leaving ? "is-leaving" : "",
        ].filter(Boolean).join(" ")}
      >
        <ProfileAvatarLink
          user={commentUser}
          userId={comment.userId}
          isCreator={isCreatorComment}
          handle={comment.handle}
          src={comment.avatar || ""}
          alt={comment.displayName || "Member"}
          className="dv-c-av-link"
          imgClassName="dv-c-av"
          size="sm"
        />
        <div className="dv-c-body">
          <div className="dv-c-meta">
            <ProfileNameLink
              user={commentUser}
              userId={comment.userId}
              isCreator={isCreatorComment}
              handle={comment.handle}
              className="dv-c-name"
            >
              {isMine ? "You" : (comment.displayName || "Member")}
            </ProfileNameLink>
            {isCreatorComment && <span className="dv-c-creator-tag">Creator</span>}
            {rank && !isCreatorComment && <span className="dv-c-opened">Opened</span>}
            <span className="dv-c-time">{formatCommentTime(comment.createdAt)}</span>
          </div>

          <div className="dv-c-bubble">{comment.text}</div>

          <div
            className={[
              "dv-c-foot",
              footConfirming ? "confirming" : "",
              footReporting ? "reporting" : "",
            ].filter(Boolean).join(" ")}
          >
            <button
              type="button"
              className={`dv-c-like-btn${iLiked ? " liked" : ""}`}
              onClick={() => handleLikeComment(comment.id)}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill={iLiked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.6l-1-1a5.5 5.5 0 00-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 000-7.8z" />
              </svg>
              {" "}{likeCount}
            </button>

            {likedByCreator && (
              <span className="dv-c-liked-by">Liked by {firstName}</span>
            )}

            {showReplyLink && (
              <button
                type="button"
                className="dv-c-reply-btn"
                onClick={() => openReplyComposer(comment)}
              >
                {hasReply ? "Edit reply" : "↳ Reply"}
              </button>
            )}

            {!isMine && currentUser && (
              <button
                type="button"
                className="dv-c-report-btn"
                onClick={() => {
                  setReportId(comment.id);
                  setConfirmDeleteId(null);
                }}
              >
                <FlagIcon />
                Report
              </button>
            )}

            {isMine && (
              <button
                type="button"
                className="dv-c-delete-btn"
                onClick={() => {
                  setConfirmDeleteId(comment.id);
                  setReportId(null);
                }}
              >
                <TrashIcon />
                Delete
              </button>
            )}

            {footConfirming && (
              <div className="dv-c-confirm-row show">
                <span>Delete this comment?</span>
                <button
                  type="button"
                  className="dv-c-confirm-btn dv-c-confirm-yes"
                  onClick={() => handleConfirmDelete(comment.id)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className="dv-c-confirm-btn dv-c-confirm-no"
                  onClick={() => setConfirmDeleteId(null)}
                >
                  Cancel
                </button>
              </div>
            )}

            {footReporting && (
              <div className="dv-c-report-row show">
                <span className="dv-c-report-label">Why are you reporting?</span>
                <div className="dv-c-report-reasons">
                  {REPORT_REASONS.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      className="dv-c-report-reason"
                      disabled={reportBusy}
                      onClick={() => handleReport(comment.id, reason)}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="dv-c-confirm-btn dv-c-confirm-no"
                  onClick={() => setReportId(null)}
                  disabled={reportBusy}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {hasReply && (
            <div className="dv-reply-wrap">
              <div className="dv-reply-item">
                <ProfileAvatarLink
                  user={{ uid: creatorId, isCreator: true }}
                  userId={creatorId}
                  isCreator
                  src={creatorAvatar || ""}
                  alt={creatorName}
                  className="dv-reply-av-link"
                  imgClassName="dv-reply-av"
                  size="sm"
                />
                <div className="dv-reply-body">
                  <div className="dv-reply-meta">
                    <span className="dv-reply-name">{creatorName}</span>
                    <span className="dv-reply-label">Creator replied</span>
                    <span className="dv-reply-time">
                      {formatCommentTime(
                        comment.creatorReply.updatedAt || comment.creatorReply.createdAt
                      )}
                    </span>
                  </div>
                  <div className="dv-reply-bubble">{comment.creatorReply.text}</div>
                </div>
              </div>
            </div>
          )}

          {replyOpenId === comment.id && (
            <div className="dv-reply-composer show">
              <input
                className="dv-rc-input"
                type="text"
                maxLength={MAX_COMMENT_LEN}
                placeholder={`Reply as ${creatorName}...`}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendReply(comment.id)}
                autoFocus
              />
              <button
                type="button"
                className="dv-rc-send"
                disabled={replyBusy || !replyText.trim()}
                onClick={() => handleSendReply(comment.id)}
              >
                {replyBusy ? "…" : "Send"}
              </button>
              <button
                type="button"
                className="dv-rc-cancel"
                onClick={() => {
                  setReplyOpenId(null);
                  setReplyText("");
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="dv-comments">
      <div className="dv-comments-title">
        {title || "Comments"}
        {!hideSub && (
          <span className="dv-comments-sub">
            {comments.length} comment{comments.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <div className="dv-comments-list">
        {visibleComments.length === 0 ? (
          <EmptyCommentLine className="dv-comments-empty" />
        ) : (
          visibleComments.map(renderComment)
        )}
      </div>

      {modNote && <div className="dv-mod-note">{modNote}</div>}

      {canPost ? (
        <div className="dv-comment-input-row">
          <input
            className="dv-comment-input"
            type="text"
            maxLength={MAX_COMMENT_LEN}
            placeholder="Add a comment... (280 max)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handlePost()}
          />
          <button type="button" className="dv-comment-send" onClick={handlePost} disabled={posting}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="white" stroke="none" aria-hidden="true">
              <path d="M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>
      ) : currentUser && !readOnly ? (
        <div className="dv-locked-comment-note">Unlock this drop to join the conversation.</div>
      ) : null}

      {toast && (
        <div className={`dv-toast dv-toast--${toast.tone === "error" ? "error" : "success"}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
